import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  type JSX,
  type ReactNode,
} from "react";

import { track } from "@/lib/analytics";
import type { EventCatalog } from "@/lib/analytics/catalog";
import type {
  OnboardingMode,
  OnboardingSituation,
  OnboardingStepName,
} from "@/lib/analytics/onboarding-steps";
import type { OnboardingContext } from "@/pages/onboarding/useOnboarding";

/**
 * Records that a NAMED onboarding step was shown.
 *
 * WHY A COMPONENT AND NOT A HOOK: the step branches in OnboardingStepContent
 * are conditional early returns, and React forbids calling a hook inside a
 * conditional. A component gets its own hook scope, so it can sit inside the
 * branch that already knows which step this is.
 *
 * WHY IT SITS IN THE BRANCH AT ALL: `viewStep` is an overloaded integer (see
 * onboarding-steps.ts). The guard on each branch — e.g.
 * `viewStep === certChoiceStep && !isAccountantFlowOwner && !isAccountantUser`
 * — IS the disambiguation, and it exists already because the branch has to
 * pick a component. Labelling here reuses that work instead of re-deriving it
 * with an inverse mapping that would silently return the wrong name.
 *
 * Renders a Fragment, so it adds nothing to the DOM.
 */
/**
 * The step currently rendering, so a CHILD component can report what blocked
 * the user without being told which step it is on.
 *
 * The alternative — threading a `step` prop down into DgiAccessStep,
 * LegalDataStep and the rest — puts the same fact in a dozen places and lets
 * it drift from the branch that actually decides it. Context keeps one source.
 */
const StepNameContext = createContext<OnboardingStepName | null>(null);

/**
 * Reports that something stopped the user on the current step.
 *
 * `code` MUST be a stable identifier, never the message shown. Messages are
 * i18n strings: they get reworded (which silently splits a code's history in
 * two) and some interpolate values — a DGI error can carry a RUC straight into
 * the analytics store.
 *
 * Outside an <OnboardingStep> this is a no-op rather than an error: these
 * components are also rendered by the accountant wizard, which is out of scope,
 * and a crash there would be a far worse outcome than a missing event.
 */
export function useOnboardingBlocked(): (code: string) => void {
  const step = useContext(StepNameContext);
  return useCallback(
    (code: string) => {
      if (step === null) return;
      try {
        track("onboarding.step.blocked", { step, code });
      } catch {
        // Never let reporting a failure cause one.
      }
    },
    [step],
  );
}

/**
 * The step currently recorded as entered, module-level and deliberately not
 * React state.
 *
 * WHY NOT MOUNT/UNMOUNT: the first version emitted on mount with
 * [name, situation, mode] deps, and both halves of that were wrong.
 *
 *  - `situation` and `mode` CHANGE while a step is open. Picking "scratch" on
 *    the situation step flips situation from "unknown", re-running the effect
 *    and fabricating a completed+entered pair — a retry that never happened,
 *    on the step where that signal matters most.
 *  - StrictMode (main.tsx:11) double-invokes effects in development, so every
 *    step emitted entered/completed/entered. Dev writes to the same database
 *    as production, so that noise is not confined to a laptop.
 *
 * Tracking the transition explicitly makes both cases no-ops: entering a step
 * that is already current emits nothing, whatever caused the re-render.
 */
/** What a move to a new step should emit. Pure, so it is testable directly. */
export interface StepTransition {
  completed: { step: OnboardingStepName; durationMs: number } | null;
  entered: OnboardingStepName | null;
}

export interface OpenStep {
  name: OnboardingStepName;
  enteredAt: number;
}

/**
 * Decides what a move from `open` to `name` emits.
 *
 * Returning nothing for an unchanged step is the load-bearing rule. The first
 * implementation emitted on mount/unmount, which double-counted twice over:
 * `situation` and `mode` CHANGE while a step is open (picking "scratch" on the
 * situation step re-ran the effect and fabricated a retry on the very step
 * where retries are the signal), and React.StrictMode (main.tsx:11)
 * double-invokes effects in development — where the portal writes to the
 * production database.
 */
export function resolveStepTransition(
  open: OpenStep | null,
  name: OnboardingStepName,
  now: number,
): StepTransition {
  if (open?.name === name) return { completed: null, entered: null };
  return {
    completed:
      open === null
        ? null
        : {
            step: open.name,
            durationMs: Math.round(now - open.enteredAt),
          },
    entered: name,
  };
}

/** Each track call is isolated: one failing must not skip the next. */
function emit<N extends "onboarding.step.entered" | "onboarding.step.completed">(
  name: N,
  props: EventCatalog[N],
): void {
  try {
    track(name, props);
  } catch {
    // Analytics must never be able to break the onboarding wizard.
  }
}

type EnterStep = (
  name: OnboardingStepName,
  situation: OnboardingSituation,
  mode: OnboardingMode,
) => void;

const EnterStepContext = createContext<EnterStep | null>(null);

/**
 * Owns the open-step state for one visit to the wizard.
 *
 * A ref inside a provider rather than module state: module state outlives the
 * wizard, so it would leak across a logout or a tenant switch, and it would
 * need a test-only reset seam. Scoping it here also makes the flush natural —
 * unmounting the provider IS leaving onboarding, which is what completes the
 * final step. Without that flush the last step of the flow never completes,
 * and a user who leaves and returns to the same step emits no `entered` at all
 * while its duration accrues the time spent away.
 *
 * In development StrictMode double-invokes this cleanup, producing one extra
 * transition per wizard mount — one pair per session rather than one per step,
 * and dev-only.
 */
export function OnboardingStepTracking({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const open = useRef<OpenStep | null>(null);

  const enterStep = useCallback<EnterStep>((name, situation, mode) => {
    const now = performance.now();
    const { completed, entered } = resolveStepTransition(open.current, name, now);
    if (entered === null) return;
    // Assigned before emitting so a failing track cannot leave the tracker
    // describing a step the user already left.
    open.current = { name: entered, enteredAt: now };
    if (completed !== null) emit("onboarding.step.completed", completed);
    emit("onboarding.step.entered", { step: entered, situation, mode });
  }, []);

  useEffect(() => {
    return () => {
      const last = open.current;
      open.current = null;
      if (last === null) return;
      emit("onboarding.step.completed", {
        step: last.name,
        durationMs: Math.round(performance.now() - last.enteredAt),
      });
    };
  }, []);

  return (
    <EnterStepContext.Provider value={enterStep}>
      {children}
    </EnterStepContext.Provider>
  );
}

export function OnboardingStep({
  name,
  onboarding,
  children,
}: {
  name: OnboardingStepName;
  /**
   * Optional: the pre-tenant steps (no_tenant, verify_company) render before
   * useOnboarding() is in scope, and genuinely have no situation or mode yet.
   * Passing a fake value would be worse than recording "unknown".
   */
  onboarding?: OnboardingContext;
  children: ReactNode;
}): JSX.Element {
  const situation = resolveSituation(onboarding);
  const mode = resolveMode(onboarding);

  const enterStep = useContext(EnterStepContext);

  useEffect(() => {
    if (enterStep === null) return;
    // situation/mode ARE dependencies, deliberately. An earlier version held
    // them in a ref to keep them out of this array, which mutated a ref during
    // render — unsafe under concurrent rendering, where a discarded render
    // would still overwrite it. Depending on them normally is correct because
    // enterOnboardingStep short-circuits on an unchanged step name, so a
    // re-run costs nothing. The design does not rely on this array being
    // minimal.
    enterStep(name, situation, mode);
    // No cleanup here: leaving a step is recorded by ENTERING the next one.
    // Per-step cleanup is exactly what StrictMode double-invoked. The wizard's
    // own unmount is flushed by leaveOnboardingStepTracking.
  }, [enterStep, name, situation, mode]);

  return (
    <StepNameContext.Provider value={name}>{children}</StepNameContext.Provider>
  );
}

function resolveSituation(
  onboarding: OnboardingContext | undefined,
): OnboardingSituation {
  const choice =
    onboarding?.situationChoice ??
    onboarding?.tenant?.onboardingSituationChoice ??
    null;
  if (
    choice === "scratch" ||
    choice === "already_issuer" ||
    choice === "issuer_need_regularization"
  ) {
    return choice;
  }
  return "unknown";
}

function resolveMode(
  onboarding: OnboardingContext | undefined,
): OnboardingMode {
  const mode = onboarding?.tenant?.onboardingMode ?? null;
  return mode === "self" || mode === "accountant" ? mode : "unknown";
}
